import Phaser from 'phaser';
import { COLORS, FONTS, FONT_SIZES } from '../config/theme';
import type { GameState, Player } from '../types/game';
import { getPlayerName } from '../utils/playerName';

/**
 * ScoreBoard / HUD overlay at the top of the GameScene.
 *
 * Styled like the reference design: compact gold-bordered bar
 * with US | THEM score boxes and turn indicator.
 */
export class ScoreBoard extends Phaser.GameObjects.Container {
  /** Pixel height of the header zone — used by GameScene.computeLayout() */
  static readonly HEADER_HEIGHT = 65;
  // Text elements
  private usScoreText!: Phaser.GameObjects.Text;
  private themScoreText!: Phaser.GameObjects.Text;
  private gameNumberText!: Phaser.GameObjects.Text;
  private turnText!: Phaser.GameObjects.Text;
  private endsText!: Phaser.GameObjects.Text;
  private messageBanner: Phaser.GameObjects.Container | null = null;

  private bgGraphics!: Phaser.GameObjects.Graphics;
  private hudContainer!: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0);

    this.createBackground();
    this.createHudBar();

    scene.add.existing(this);
    this.setDepth(100);
  }

  private createBackground(): void {
    this.bgGraphics = this.scene.add.graphics();
    const w = this.scene.scale.width;
    // Dark background strip
    this.bgGraphics.fillStyle(COLORS.bgPrimary, 0.9);
    this.bgGraphics.fillRect(0, 0, w, ScoreBoard.HEADER_HEIGHT);
    this.add(this.bgGraphics);
  }

  private createHudBar(): void {
    const w = this.scene.scale.width;
    const cx = w / 2;

    this.hudContainer = this.scene.add.container(cx, 0);
    this.add(this.hudContainer);

    // ── Gold-bordered HUD card ──
    // Caps at 320px on wide screens; shrinks on narrow ones (phones/tablets)
    const hudW = Math.min(320, Math.round(w * 0.45));
    const hudH = 42;
    const hudGfx = this.scene.add.graphics();

    // Background
    hudGfx.fillStyle(COLORS.bgPrimary, 1);
    hudGfx.fillRoundedRect(-hudW / 2, 4, hudW, hudH, 6);
    // Gold border
    hudGfx.lineStyle(2, COLORS.gold, 0.8);
    hudGfx.strokeRoundedRect(-hudW / 2, 4, hudW, hudH, 6);
    // Vertical divider
    hudGfx.lineStyle(1, COLORS.gold, 0.5);
    hudGfx.beginPath();
    hudGfx.moveTo(0, 8);
    hudGfx.lineTo(0, hudH);
    hudGfx.strokePath();

    this.hudContainer.add(hudGfx);

    // "US" label
    const usLabel = this.scene.add.text(-hudW / 4, 10, 'US', {
      fontSize: FONT_SIZES.xs,
      color: COLORS.textGold,
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.hudContainer.add(usLabel);

    // US score value
    this.usScoreText = this.scene.add.text(-hudW / 4, 24, '0', {
      fontSize: FONT_SIZES.large,
      color: COLORS.textWhite,
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.hudContainer.add(this.usScoreText);

    // "THEM" label
    const themLabel = this.scene.add.text(hudW / 4, 10, 'THEM', {
      fontSize: FONT_SIZES.xs,
      color: COLORS.textGold,
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.hudContainer.add(themLabel);

    // THEM score value
    this.themScoreText = this.scene.add.text(hudW / 4, 24, '0', {
      fontSize: FONT_SIZES.large,
      color: COLORS.textWhite,
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.hudContainer.add(this.themScoreText);

    // ── Turn indicator (below HUD bar) ──
    this.turnText = this.scene.add.text(cx, 50, '', {
      fontSize: FONT_SIZES.xs,
      color: COLORS.textGoldBright,
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5, 0);
    this.add(this.turnText);

    // ── Game number pill (left side) ──
    const pillH    = 26;
    const pillPadX = 12;
    const pillW    = 80;
    const pillY    = 7;
    const pillX    = 10;

    const pillGfx = this.scene.add.graphics();
    // Solid dark base — clearly distinct from the felt beneath
    pillGfx.fillStyle(0x080808, 0.96);
    pillGfx.fillRoundedRect(pillX, pillY, pillW, pillH, 7);
    // Gold inner glow layer
    pillGfx.fillStyle(COLORS.gold, 0.06);
    pillGfx.fillRoundedRect(pillX, pillY, pillW, pillH, 7);
    // Gold border — clearly visible
    pillGfx.lineStyle(1.5, COLORS.gold, 0.90);
    pillGfx.strokeRoundedRect(pillX, pillY, pillW, pillH, 7);
    this.add(pillGfx);

    this.gameNumberText = this.scene.add.text(
      pillX + pillPadX,
      pillY + pillH / 2,
      'Game 1',
      {
        fontSize: FONT_SIZES.xs,
        color: COLORS.textGold,
        fontStyle: 'bold',
        fontFamily: FONTS.body,
      }
    ).setOrigin(0, 0.5);
    this.add(this.gameNumberText);

    // ── Board ends (right side) ──
    this.endsText = this.scene.add.text(w - 12, 8, '', {
      fontSize: FONT_SIZES.xs,
      color: COLORS.textGrayLight,
      fontFamily: FONTS.body,
    }).setOrigin(1, 0);
    this.add(this.endsText);
  }

  // ─── Update Methods ──────────────────────────────────────

  /** Refresh all HUD elements from game state */
  update(
    gameState: GameState,
    myPosition: number,
    teamScores?: Record<string, number>
  ): void {
    // Session scores — "US" = my team, "THEM" = opponent team
    if (teamScores) {
      const myTeam = myPosition % 2;
      const oppTeam = myTeam === 0 ? 1 : 0;
      const myScore = teamScores[String(myTeam)] ?? 0;
      const oppScore = teamScores[String(oppTeam)] ?? 0;
      this.usScoreText.setText(String(myScore));
      this.themScoreText.setText(String(oppScore));
    }

    // Game number
    this.gameNumberText.setText(`Game ${gameState.game_number}`);

    // Board ends
    if (gameState.board_left_end !== null && gameState.board_right_end !== null) {
      this.endsText.setText(`[${gameState.board_left_end}] ── [${gameState.board_right_end}]`);
    } else {
      this.endsText.setText('');
    }

    // Turn indicator
    this.highlightCurrentTurn(gameState.current_turn, myPosition, gameState.players);
  }

  /** Update turn indicator text */
  highlightCurrentTurn(
    currentTurn: number,
    myPosition: number,
    players: Player[]
  ): void {
    const isMyTurn = currentTurn === myPosition;

    if (isMyTurn) {
      this.turnText.setText('YOUR TURN');
      this.turnText.setColor(COLORS.textGreen);
    } else {
      const currentPlayer = players.find((p) => p.position === currentTurn);
      const name = getPlayerName(currentPlayer, `Player ${currentTurn}`);
      this.turnText.setText(`${name}'s turn`);
      this.turnText.setColor(COLORS.textGold);
    }
  }

  /** Show a temporary banner message */
  showMessage(text: string, duration = 2000): void {
    this.hideMessage();

    const w = this.scene.scale.width;
    const container = this.scene.add.container(w / 2, 100);

    const bg = this.scene.add.graphics();
    const textWidth = text.length * 10 + 40;
    bg.fillStyle(COLORS.bgCard, 0.95);
    bg.fillRoundedRect(-textWidth / 2, -20, textWidth, 40, 10);
    bg.lineStyle(1, COLORS.gold, 0.6);
    bg.strokeRoundedRect(-textWidth / 2, -20, textWidth, 40, 10);
    container.add(bg);

    const msg = this.scene.add.text(0, 0, text, {
      fontSize: FONT_SIZES.medium,
      color: COLORS.textGoldBright,
      fontStyle: 'bold',
      fontFamily: FONTS.body,
    }).setOrigin(0.5);
    container.add(msg);

    container.setDepth(200);
    this.messageBanner = container;

    this.scene.time.delayedCall(duration, () => {
      this.scene.tweens.add({
        targets: container,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          container.destroy();
          if (this.messageBanner === container) {
            this.messageBanner = null;
          }
        },
      });
    });
  }

  private hideMessage(): void {
    if (this.messageBanner) {
      this.messageBanner.destroy();
      this.messageBanner = null;
    }
  }

  /**
   * Full rebuild on resize — recalculates seat widths, HUD card width, and
   * all x-positions for the new viewport.  GameScene calls update() afterward
   * to repopulate scores and turn state.
   */
  handleResize(_width: number): void {
    // Destroy all children and the message banner, then rebuild from scratch.
    this.removeAll(true);
    if (this.messageBanner) {
      this.messageBanner.destroy();
      this.messageBanner = null;
    }
    this.createBackground();
    this.createHudBar();
  }
}
